import { useEffect, useMemo, useState } from "react";

import type { DeviceType } from "@/components/filters/DeviceTypeFilter";
import { useRequestAnalyticsQuery } from "@/hooks/useRequestAnalyticsQuery";
import i18n from "@/i18n/i18n";
import { AnalyticsServiceV2 } from "@/services/features/AnalyticsServiceV2";
import { AnalyticsCardData, DateRange } from "@/services/StorePerformanceService";
import { Store } from "@/types/store";
import {
  CurrencyCode,
  formatCurrencyWithNoDecimals,
  getStoreCurrencyIdentifiers,
} from "@/utils/currency";
import { formatPercentFromWhole } from "@/utils/number";
import { createLowerIsBetterTrend, type LowerIsBetterTrend } from "@/utils/analyticsTrend";

export type MissedRequestsTrend = LowerIsBetterTrend;

export interface MissedRequestsData extends AnalyticsCardData {
  percentageTrend: MissedRequestsTrend;
  monetaryTrend: MissedRequestsTrend;
  percentageComparisonProgress?: string;
}

interface MissedRequestsRawData {
  percentMissed: number;
  missedRequests: number;
  totalRequests: number;
  totalRequestValue: number;
}

export const useMissedRequests = (
  stores: Store[] | null,
  dateRange?: DateRange,
  deviceType?: DeviceType,
  comparisonDateRange?: DateRange,
  areaIds?: number[],
): MissedRequestsData => {
  const currentLanguage = i18n.resolvedLanguage || i18n.language;
  const [rawData, setRawData] = useState<MissedRequestsRawData | null>(null);
  const [comparisonRawData, setComparisonRawData] = useState<MissedRequestsRawData | null>();
  const [loading, setLoading] = useState(false);
  const [completedRequestKey, setCompletedRequestKey] = useState<string | null>(null);
  const [currencyCode, currencySymbol] = getStoreCurrencyIdentifiers(stores);
  const {
    beginRequest,
    comparisonEndDate,
    comparisonStartDate,
    hasComparisonRequestInputs,
    hasRequestInputs,
    requestKey,
    baseParams,
    endDate,
    startDate,
  } = useRequestAnalyticsQuery({
    stores,
    dateRange,
    deviceType,
    comparisonDateRange,
    areaIds,
  });

  useEffect(() => {
    const isCurrent = beginRequest();
    if (!baseParams || !requestKey) {
      setRawData(null);
      setComparisonRawData(null);
      setLoading(false);
      setCompletedRequestKey(null);
      return;
    }

    const fetchRange = async (
      rangeStartDate: string,
      rangeEndDate: string,
    ): Promise<MissedRequestsRawData | null> => {
      const analyticsService = AnalyticsServiceV2.getInstance();
      const params: Parameters<typeof analyticsService.getRequestAnalytics>[0] = {
        ...baseParams,
        startDate: rangeStartDate,
        endDate: rangeEndDate,
      };
      const totalResponse = await analyticsService.getRequestAnalytics(params);
      const totalRequests = totalResponse?.data?.totalRequests ?? 0;

      if (totalRequests <= 0) {
        return null;
      }

      const missedResponse = await analyticsService.getRequestAnalytics({
        ...params,
        requestStatus: "MISSED",
      });
      const missedRequests = missedResponse?.data?.totalRequests ?? 0;
      return {
        percentMissed: (missedRequests / totalRequests) * 100,
        missedRequests,
        totalRequests,
        totalRequestValue: Math.round(missedResponse?.data?.totalRequestValue ?? 0),
      };
    };

    const fetchMissedRequests = async () => {
      setLoading(true);
      setComparisonRawData(hasComparisonRequestInputs ? undefined : null);
      try {
        try {
          const currentData = await fetchRange(startDate, endDate);
          if (isCurrent()) setRawData(currentData);
        } catch (error) {
          console.error("Error fetching missed requests:", error);
          if (isCurrent()) setRawData(null);
          return;
        }

        if (hasComparisonRequestInputs && comparisonStartDate && comparisonEndDate) {
          try {
            const comparisonData = await fetchRange(comparisonStartDate, comparisonEndDate);
            if (isCurrent()) setComparisonRawData(comparisonData);
          } catch (error) {
            console.error("Error fetching missed requests comparison:", error);
            if (isCurrent()) setComparisonRawData(null);
          }
        }
      } finally {
        if (isCurrent()) {
          setLoading(false);
          setCompletedRequestKey(requestKey);
        }
      }
    };

    void fetchMissedRequests();
  }, [
    beginRequest,
    comparisonEndDate,
    comparisonStartDate,
    hasComparisonRequestInputs,
    requestKey,
    baseParams,
  ]);

  const isRequestPending = hasRequestInputs && completedRequestKey !== requestKey;

  return useMemo(() => {
    if (!rawData) {
      const trend = hasComparisonRequestInputs
        ? createLowerIsBetterTrend(
            null,
            comparisonRawData === undefined
              ? undefined
              : (comparisonRawData?.percentMissed ?? null),
          )
        : { status: "unavailable" as const };
      return {
        previousValue: null,
        isPositive: true,
        pillText: "",
        subtitle: i18n.t("common.analytics.noDataForTimeframe"),
        value: "--",
        rawValue: null,
        loading: loading || isRequestPending,
        percentageTrend: trend,
        monetaryTrend: hasComparisonRequestInputs
          ? createLowerIsBetterTrend(
              null,
              comparisonRawData === undefined
                ? undefined
                : (comparisonRawData?.totalRequestValue ?? null),
            )
          : { status: "unavailable" },
      };
    }

    const value = formatCurrencyWithNoDecimals(
      rawData.totalRequestValue,
      currencyCode as CurrencyCode,
      currencySymbol,
    );
    const formattedPercent = formatPercentFromWhole(rawData.percentMissed, currentLanguage);

    return {
      previousValue: null,
      value: formattedPercent,
      subtitle: i18n.t("common.analytics.itemRequestsValue", { value }),
      isPositive: rawData.missedRequests === 0,
      pillText: i18n.t("common.analytics.requestsProgress", {
        current: rawData.missedRequests,
        total: rawData.totalRequests,
      }),
      rawValue: rawData.percentMissed,
      loading: loading || isRequestPending,
      percentageTrend: hasComparisonRequestInputs
        ? createLowerIsBetterTrend(
            rawData.percentMissed,
            comparisonRawData === undefined
              ? undefined
              : (comparisonRawData?.percentMissed ?? null),
          )
        : { status: "unavailable" },
      percentageComparisonProgress:
        comparisonRawData && comparisonRawData.totalRequests > 0
          ? i18n.t("common.analytics.requestsProgress", {
              current: comparisonRawData.missedRequests,
              total: comparisonRawData.totalRequests,
            })
          : undefined,
      monetaryTrend: hasComparisonRequestInputs
        ? createLowerIsBetterTrend(
            rawData.totalRequestValue,
            comparisonRawData === undefined
              ? undefined
              : (comparisonRawData?.totalRequestValue ?? null),
          )
        : { status: "unavailable" },
    };
  }, [
    rawData,
    comparisonRawData,
    currencyCode,
    currencySymbol,
    currentLanguage,
    hasComparisonRequestInputs,
    loading,
    isRequestPending,
  ]);
};
